import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { NumberFormatter } from '@repo/toolkit/number'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import Typography from '@repo/ui/typography'
import { TFunction } from 'i18next'

import { leaderFreeRateEnumMapping } from '../../../lib/types/enums/activity/leader-free-rate.enum'
import { transportModeEnumMapping } from '../../../lib/types/enums/activity/transport-mode.enum'
import { CurrencyEnum } from '../../../lib/types/enums/common/currency.enum'
import { Activity } from '../../../lib/types/models/ol/activity/activity'
import { ActivityInput } from '../../../lib/types/models/ol/activity/activity-input'
import { formatFullAddress } from '../../../lib/utils/functions/address.functions'
import { isHostFamilyDayActivity } from '../../../utils/constants/activity.constants'

export const mapActivityToInput = (activity: Activity): ActivityInput => ({
  name: activity.name,
  locationName: activity.locationName,
  address1: activity.address1,
  address2: activity.address2,
  city: activity.city,
  zipCode: activity.zipCode,
  country: activity.country,
  phoneNumber: activity.phoneNumber,
  activitySplitAge: activity.activitySplitAge,
  activityLeaderFreeRatio: activity.activityLeaderFreeRatio,
  activityChildCost: activity.activityChildCost,
  activityAdultCost: activity.activityAdultCost,
  activityLeaderCost: activity.activityLeaderCost,
  activityFlatGroupCost: activity.activityFlatGroupCost,
  transportSplitAge: activity.transportSplitAge,
  transportMode: activity.transportMode,
  transportChildCost: activity.transportChildCost,
  transportAdultCost: activity.transportAdultCost,
  transportLeaderCost: activity.transportLeaderCost,
  transportFlatGroupCost: activity.transportFlatGroupCost,
  comments: activity.comments
})

// "Host family day" has no location and no costs: every column except the name shows "-".
const withHostFamilyDayCell = (column: Column<Activity>): Column<Activity> => {
  if (column.accessorKey === 'name') return column

  const { cell } = column
  return {
    ...column,
    cell: (context) => {
      if (isHostFamilyDayActivity(context.row.original)) return '-'
      if (typeof cell === 'function') return cell(context)
      return cell ?? context.getValue()
    }
  }
}

export const activityColumns = (
  t: TFunction<AvailableLanguages, undefined>,
  currency: CurrencyEnum
): Column<Activity>[] => [
  {
    accessorKey: 'name',
    header: t('activity.table.name'),
    enableSorting: true
  },
  {
    accessorKey: 'address1',
    header: 'Address',
    enableSorting: true,
    cell: ({ row }) => formatFullAddress(row.original) || '-'
  },
  {
    accessorKey: 'activitySplitAge',
    columnHeaderClassName: 'bg-yellow-light',
    header: t('activity.table.activitySplitAge'),
    enableSorting: true,
    cell: ({ row }) => (
      <Typography variant="label-medium" color={tailwindColors.yellow.DEFAULT}>
        {row.original.activitySplitAge || '-'}
      </Typography>
    )
  },

  {
    accessorKey: 'activityChildCost',
    columnHeaderClassName: 'bg-yellow-light',
    header: t('activity.table.ChildCost'),
    enableSorting: true,
    cell: ({ row }) => (
      <Badge
        variant="yellow"
        text={NumberFormatter.formatCurrency(row.original.activityChildCost ?? 0, currency)}
      />
    )
  },
  {
    accessorKey: 'activityAdultCost',
    columnHeaderClassName: 'bg-yellow-light',
    header: t('activity.table.AdultCost'),
    enableSorting: true,
    cell: ({ row }) => (
      <Badge
        variant="yellow"
        text={NumberFormatter.formatCurrency(row.original.activityAdultCost ?? 0, currency)}
      />
    )
  },
  {
    accessorKey: 'activityLeaderCost',
    columnHeaderClassName: 'bg-yellow-light',
    header: t('activity.table.LeaderCost'),
    enableSorting: true,
    cell: ({ row }) => (
      <Badge
        variant="yellow"
        text={NumberFormatter.formatCurrency(row.original.activityLeaderCost ?? 0, currency)}
      />
    )
  },
  {
    accessorKey: 'activityLeaderFreeRatio',
    columnHeaderClassName: 'bg-yellow-light',
    header: t('activity.table.leaderFreeRatio'),
    enableSorting: true,
    cell: ({ row }) => {
      const leaderFreeRate = leaderFreeRateEnumMapping[row.original.activityLeaderFreeRatio]
      return (
        <Typography variant="label-medium" color={tailwindColors.yellow.DEFAULT}>
          {leaderFreeRate ? t(leaderFreeRate) : '-'}
        </Typography>
      )
    }
  },
  {
    accessorKey: 'activityFlatGroupCost',
    columnHeaderClassName: 'bg-yellow-light',
    header: t('activity.table.FlatGroupCost'),
    enableSorting: true,
    cell: ({ row }) => (
      <Badge
        variant="yellow"
        text={NumberFormatter.formatCurrency(row.original.activityFlatGroupCost ?? 0, currency)}
      />
    )
  },
  {
    accessorKey: 'transportMode',
    header: t('activity.table.transportMode'),
    enableSorting: true,
    cell: ({ row }) => {
      const mode = transportModeEnumMapping[row.original.transportMode]
      return mode ? t(mode) : '-'
    }
  },
  {
    accessorKey: 'transportSplitAge',
    header: t('activity.table.transportSplitAge'),
    columnHeaderClassName: 'bg-fushia-light',
    enableSorting: true,
    cell: ({ row }) => (
      <Typography variant="label-medium" color={tailwindColors.fushia.hover}>
        {row.original.transportSplitAge || '-'}
      </Typography>
    )
  },
  {
    accessorKey: 'transportChildCost',
    header: t('activity.table.ChildCost'),
    enableSorting: true,
    columnHeaderClassName: 'bg-fushia-light',
    cell: ({ row }) => (
      <Badge
        variant="fushia"
        text={NumberFormatter.formatCurrency(row.original.transportChildCost ?? 0, currency)}
      />
    )
  },
  {
    accessorKey: 'transportAdultCost',
    header: t('activity.table.AdultCost'),
    enableSorting: true,
    columnHeaderClassName: 'bg-fushia-light',
    cell: ({ row }) => (
      <Badge
        variant="fushia"
        text={NumberFormatter.formatCurrency(row.original.transportAdultCost ?? 0, currency)}
      />
    )
  },
  {
    accessorKey: 'transportLeaderCost',
    header: t('activity.table.LeaderCost'),
    enableSorting: true,
    columnHeaderClassName: 'bg-fushia-light',
    cell: ({ row }) => (
      <Badge
        variant="fushia"
        text={NumberFormatter.formatCurrency(row.original.transportLeaderCost ?? 0, currency)}
      />
    )
  },
  {
    accessorKey: 'transportFlatGroupCost',
    header: t('activity.table.FlatGroupCost'),
    columnHeaderClassName: 'bg-fushia-light',
    enableSorting: true,
    cell: ({ row }) => (
      <Badge
        variant="fushia"
        text={NumberFormatter.formatCurrency(row.original.transportFlatGroupCost ?? 0, currency)}
      />
    )
  },
  {
    accessorKey: 'comments',
    header: t('activity.table.comments'),
    enableSorting: true,
    cell: ({ row }) => row.original.comments || '-'
  }
]

export const getActivityColumns = (
  t: TFunction<AvailableLanguages, undefined>,
  currency: CurrencyEnum
): Column<Activity>[] => {
  return activityColumns(t, currency).map(withHostFamilyDayCell)
}
